import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { TargetingIcon } from "orion-components/SharedComponents";
import PropTypes from "prop-types";
import MainMenu from "./MainMenu/MainMenu";
import { getDir } from "orion-components/i18n/Config/selector";
import { Translate, getTranslation } from "orion-components/i18n";
import { SvgIcon, Tooltip } from "@mui/material";
import { PlayCircleFilledWhite, PauseCircleFilled } from "@mui/icons-material";
import { mdiFullscreen, mdiFullscreenExit } from "@mdi/js";
import { useMemo } from "react";
import { getEcoSettingByKey } from "orion-components/Services/EcoSettingService/selectors";
import { makeSelectComponentProp } from "../../../SharedComponents/ComponentState/redux/selectors";

// Constants
const VIDEO_MODES = {
	LIVE: "Live",
	RECORDED: "Recorded"
};

const COLORS = {
	WHITE: "#FFFFFF",
	LIVE_INDICATOR: "#21C230",
	RECORDED_INDICATOR: "#BB3C1E",
	BACKGROUND_OVERLAY: "rgba(0, 0, 0, 0.5)",
	TRANSPARENT: "transparent"
};

const DIMENSIONS = {
	TOP_BAR_HEIGHT: 48,
	ICON_SIZE: 18,
	FULLSCREEN_ICON_SIZE: 24,
	TOOLTIP_OFFSET: -30,
	Z_INDEX_BASE: 1904,
	Z_INDEX_WRAPPER: 1904,
	Z_INDEX_LIVE_INDICATOR: 1905
};

const COMPONENT_KEY = "cbComponents.newVideoPlayer.videoPlayerContainer";

const VideoTopBar = ({
	camera,
	namePrefix,
	dockedItemId,
	isDockedVideoTour,
	tourMode,
	tourPaused,
	getSelectedVideoMode,
	isInFullScreen,
	expanded,
	inDock,
	isInVideoWall,
	removeFromWall,
	toggleFullScreenCameraDialog,
	currentVideoMode,
	clusterProps,
	isOnAlertProfile,
	hideRemoveFromWall,
	setTourPaused,
	videoPlayerHovered = false,
	instanceId,
	disableVideoModeToggle,
	onBookmarkSelect,
	onClipSelect,
	dialogContainerRef
}) => {
	const { entityData } = camera;
	const baseCameraName = namePrefix ? `${namePrefix}: ${entityData.properties.name}` : entityData.properties.name;
	const presetText = getTranslation("global.profiles.widgets.cameras.camCard.presets");
	const cameraName =
		tourMode && camera?.preset ? `${baseCameraName} (${presetText} ${camera.preset})` : baseCameraName;
	const [isMenuOpen, setIsMenuOpen] = useState(false);
	const dir = useSelector(state => getDir(state));
	const [switchVideoMode, setSwitchVideoMode] = useState(VIDEO_MODES.LIVE);
	const videoPlayerAlwaysShowName = useSelector(
		state => getEcoSettingByKey("videoPlayerAlwaysShowName")(state) || false
	);

	const isJumpToDifferentTimeOpen = useSelector(
		makeSelectComponentProp(COMPONENT_KEY, instanceId, "components.recorded.jumpToDifferentTime.isOpen")
	);

	// Derived state
	const shouldShowTopBarContent = (videoPlayerAlwaysShowName || videoPlayerHovered) && !isJumpToDifferentTimeOpen;
	const shouldShowMenuAndTargeting = videoPlayerHovered && !isJumpToDifferentTimeOpen;
	const isLiveMode = switchVideoMode === VIDEO_MODES.LIVE;

	const getMenuState = value => {
		setIsMenuOpen(value);
	};

	const handleTourPauseToggle = () => {
		setTourPaused(!tourPaused);
	};

	const handleVideoModeToggle = () => {
		if (disableVideoModeToggle) return;
		const newMode = isLiveMode ? VIDEO_MODES.RECORDED : VIDEO_MODES.LIVE;
		setSwitchVideoMode(newMode);
		getSelectedVideoMode(newMode);
	};

	const handleFullScreenToggle = () => {
		toggleFullScreenCameraDialog(!isInFullScreen);
	};

	useEffect(() => {
		if (currentVideoMode !== "" && currentVideoMode !== switchVideoMode) {
			setSwitchVideoMode(currentVideoMode);
		}
	}, [currentVideoMode, switchVideoMode]);

	const styles = useMemo(() => {
		const isRtl = dir === "rtl";

		return {
			// ...existing styles object...
			container: {
				position: "absolute",
				top: 0,
				left: 0,
				width: "100%",
				zIndex: DIMENSIONS.Z_INDEX_BASE
			},
			topBarWrapper: {
				zIndex: DIMENSIONS.Z_INDEX_WRAPPER,
				display: "flex",
				flexDirection: "row",
				fontSize: 12,
				width: "100%",
				height: DIMENSIONS.TOP_BAR_HEIGHT,
				backgroundColor: shouldShowTopBarContent ? COLORS.BACKGROUND_OVERLAY : COLORS.TRANSPARENT,
				alignItems: "center",
				justifyContent: "end"
			},
			menuAndTargetingContainer: {
				display: shouldShowMenuAndTargeting ? "flex" : "none",
				flexDirection: "row"
			},
			cameraNameContainer: {
				display: shouldShowTopBarContent ? "flex" : "none",
				alignItems: "center",
				justifyContent: "center",
				flex: 1,
				overflow: "hidden",
				textOverflow: "ellipsis",
				margin: "0 8px"
			},
			cameraNameText: {
				whiteSpace: "nowrap",
				overflow: "hidden",
				textOverflow: "ellipsis",
				width: "auto",
				fontSize: "1em",
				textTransform: "uppercase",
				color: COLORS.WHITE
			},
			modeAndFullScreenContainer: {
				display: "flex",
				flexDirection: "row",
				alignItems: "center"
			},
			tourPlayPauseIcon: {
				color: COLORS.WHITE,
				cursor: "pointer",
				display: videoPlayerHovered ? "flex" : "none",
				alignItems: "center",
				height: DIMENSIONS.FULLSCREEN_ICON_SIZE,
				lineHeight: 0,
				...(isRtl ? { marginLeft: 8 } : { marginRight: 8 })
			},
			videoModeText: {
				fontSize: "1em",
				textTransform: "uppercase",
				color: COLORS.WHITE,
				cursor: disableVideoModeToggle ? "default" : "pointer",
				display: videoPlayerHovered ? "block" : "none",
				...(isRtl ? { marginLeft: 8 } : { marginRight: 8 })
			},
			liveIndicator: {
				backgroundColor: isLiveMode ? COLORS.LIVE_INDICATOR : COLORS.RECORDED_INDICATOR,
				...(isRtl ? { marginLeft: "4px" } : { marginRight: "4px" })
			},
			fullScreenButton: {
				display: "flex",
				alignItems: "center"
			},
			fullScreenIcon: {
				width: `${DIMENSIONS.FULLSCREEN_ICON_SIZE}px`,
				height: `${DIMENSIONS.FULLSCREEN_ICON_SIZE}px`,
				color: COLORS.WHITE,
				cursor: "pointer"
			},
			targetingIconContainer: {
				width: DIMENSIONS.ICON_SIZE,
				height: DIMENSIONS.ICON_SIZE
			}
		};
	}, [
		dir,
		shouldShowTopBarContent,
		shouldShowMenuAndTargeting,
		videoPlayerHovered,
		isLiveMode,
		disableVideoModeToggle
	]);

	const tooltipProps = {
		title: cameraName,
		placement: "bottom",
		slotProps: {
			popper: {
				modifiers: [
					{
						name: "offset",
						options: {
							offset: [0, DIMENSIONS.TOOLTIP_OFFSET]
						}
					}
				]
			}
		}
	};

	const targetingIconProps = {
		geometry: entityData.geometry,
		id: camera.id,
		feedId: camera.feedId,
		customStyles: styles.targetingIconContainer,
		iconContainerStyle: { padding: 0 }
	};

	return (
		<div dir={dir} className="bccVideoTopBar" style={styles.container}>
			<div className="bccVideoTopBarWrapper" style={styles.topBarWrapper} data-testid="orion-components-cbcomponents-new-video-player-components-video-top-bar-div">
				{/* Menu and Targeting Section */}
				<div id="menuAndTargetingIcon" style={styles.menuAndTargetingContainer}>
					<MainMenu
						getMenuState={getMenuState}
						camera={camera}
						dockedItemId={dockedItemId}
						isDockedVideoTour={isDockedVideoTour}
						inDock={inDock}
						tourMode={tourMode}
						isInVideoWall={isInVideoWall}
						removeFromWall={removeFromWall}
						clusterProps={clusterProps}
						isOnAlertProfile={isOnAlertProfile}
						hideRemoveFromWall={hideRemoveFromWall}
						instanceId={instanceId}
						onBookmarkSelect={onBookmarkSelect}
						onClipSelect={onClipSelect}
						dialogContainerRef={dialogContainerRef} data-testid="orion-components-cbcomponents-new-video-player-components-video-top-bar-main-menu"
					/>

					{!isMenuOpen && !isInFullScreen && <TargetingIcon {...targetingIconProps} />}
				</div>

				{/* Camera Name Section */}
				<div id="cameraName" style={styles.cameraNameContainer}>
					<Tooltip {...tooltipProps} data-testid="orion-components-cbcomponents-new-video-player-components-video-top-bar-tooltip">
						<div style={styles.cameraNameText}>{cameraName}</div>
					</Tooltip>
				</div>

				{/* Mode and Full Screen Section */}
				<div id="modeAndFullScreen" style={styles.modeAndFullScreenContainer}>
					{tourMode && (
						<div style={styles.tourPlayPauseIcon} onClick={handleTourPauseToggle} data-testid="orion-components-cbcomponents-new-video-player-components-video-top-bar-div-2">
							{tourPaused ? (
								<Tooltip
									title={getTranslation(
										"global.CBComponents.newVideoPlayer.components.videoTopBar.resumeTourTooltip"
									)}
									placement="bottom" data-testid="orion-components-cbcomponents-new-video-player-components-video-top-bar-tooltip-2"
								>
									<PlayCircleFilledWhite />
								</Tooltip>
							) : (
								<Tooltip
									title={getTranslation(
										"global.CBComponents.newVideoPlayer.components.videoTopBar.pauseTourTooltip"
									)}
									placement="bottom" data-testid="orion-components-cbcomponents-new-video-player-components-video-top-bar-tooltip-3"
								>
									<PauseCircleFilled />
								</Tooltip>
							)}
						</div>
					)}

					<div style={styles.videoModeText} onClick={handleVideoModeToggle} data-testid="orion-components-cbcomponents-new-video-player-components-video-top-bar-div-3">
						{isLiveMode ? (
							<Translate value="global.CBComponents.newVideoPlayer.components.videoTopBar.live" />
						) : (
							<Translate value="global.CBComponents.newVideoPlayer.components.videoTopBar.recorded" />
						)}
					</div>

					<div className="bcc_liveIndicator" style={styles.liveIndicator} />

					<div style={styles.fullScreenButton}>
						<SvgIcon onClick={handleFullScreenToggle} style={styles.fullScreenIcon} data-testid="orion-components-cbcomponents-new-video-player-components-video-top-bar-svg-icon">
							<path d={!isInFullScreen ? mdiFullscreen : mdiFullscreenExit} />
						</SvgIcon>
					</div>
				</div>
			</div>
		</div>
	);
};

VideoTopBar.propTypes = {
	camera: PropTypes.shape({
		entityData: PropTypes.shape({
			properties: PropTypes.shape({
				name: PropTypes.string.isRequired
			}).isRequired,
			geometry: PropTypes.object.isRequired
		}).isRequired,
		id: PropTypes.string.isRequired,
		feedId: PropTypes.string.isRequired,
		preset: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
	}).isRequired,
	namePrefix: PropTypes.string,
	dockedItemId: PropTypes.string,
	isDockedVideoTour: PropTypes.bool,
	tourMode: PropTypes.bool,
	tourPaused: PropTypes.bool,
	getSelectedVideoMode: PropTypes.func.isRequired,
	isInFullScreen: PropTypes.bool,
	expanded: PropTypes.bool,
	inDock: PropTypes.bool,
	isInVideoWall: PropTypes.bool,
	removeFromWall: PropTypes.func,
	toggleFullScreenCameraDialog: PropTypes.func.isRequired,
	currentVideoMode: PropTypes.string,
	clusterProps: PropTypes.object,
	isOnAlertProfile: PropTypes.bool,
	hideRemoveFromWall: PropTypes.bool,
	setTourPaused: PropTypes.func,
	videoPlayerHovered: PropTypes.bool,
	instanceId: PropTypes.string,
	disableVideoModeToggle: PropTypes.bool,
	onBookmarkSelect: PropTypes.func,
	onClipSelect: PropTypes.func,
	dialogContainerRef: PropTypes.shape({
		current: PropTypes.any
	})
};

VideoTopBar.defaultProps = {
	tourMode: false,
	dockedItemId: null,
	isDockedVideoTour: false,
	tourPaused: false,
	isInFullScreen: false,
	expanded: false,
	inDock: false,
	isInVideoWall: false,
	currentVideoMode: "",
	isOnAlertProfile: false,
	hideRemoveFromWall: false,
	videoPlayerHovered: false,
	disableVideoModeToggle: false,
	removeFromWall: () => {},
	clusterProps: null
};

export default VideoTopBar;
